const SIMPLE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const MAX_OTP_FAILED_ATTEMPTS = 5;

export const formatMaskedPhone = (last4: string): string => `••• ••• ${last4}`;

/**
 * Staff masquerade username: superuserEmail->travelerEmail
 * e.g. kyru@zicasso.com->kyru+traveler@zicasso.com
 */
const MASQUERADE_EMAIL = /^([^\s@]+@[^\s@]+\.[^\s@]+)->([^\s@]+@[^\s@]+\.[^\s@]+)$/;

export const isMasqueradeEmail = (value: string): boolean => MASQUERADE_EMAIL.test(value.trim());

export const isValidLoginEmail = (value: string): boolean => {
  const trimmed = value.trim();
  return SIMPLE_EMAIL.test(trimmed) || MASQUERADE_EMAIL.test(trimmed);
};

export const parseMasqueradeEmail = (
  value: string,
): { superEmail: string; travelerEmail: string } | null => {
  const match = value.trim().match(MASQUERADE_EMAIL);
  if (!match) {
    return null;
  }
  return { superEmail: match[1], travelerEmail: match[2] };
};

/**
 * Post-identification branch point for the login flow.
 * Stubbed until travel-service returns the preferred access method (ETS-2326).
 */
export type LoginMethod = 'email_verification' | 'password_verification';

/** High-level login flow stages rendered by LoginContainer. */
export type LoginStep =
  | 'user_identification'
  | 'email_verification'
  | 'sms_verification'
  | 'password_verification'
  | 'confirm_phone';

/** Discrete pathnames per step so analytics (GTM/Hotjar) get unique page_path values. */
export const LOGIN_STEP_PATH: Record<LoginStep, string> = {
  user_identification: '/login',
  email_verification: '/login/email-verification',
  sms_verification: '/login/sms-verification',
  password_verification: '/login/password-verification',
  confirm_phone: '/login/confirm-phone',
};

export const getLoginStepFromPathname = (pathname: string): LoginStep => {
  const normalized = pathname.replace(/\/+$/, '') || '/login';
  const match = (Object.entries(LOGIN_STEP_PATH) as [LoginStep, string][])
    .find(([, path]) => path === normalized);
  return match?.[0] ?? 'user_identification';
};

export const buildLoginPath = (
  step: LoginStep,
  searchParams?: URLSearchParams,
): string => {
  const path = LOGIN_STEP_PATH[step];
  const query = searchParams?.toString();
  return query ? `${path}?${query}` : path;
};

export type GetDefaultLoginMethod = (email: string) => Promise<LoginMethod>;

export const getDefaultLoginMethod: GetDefaultLoginMethod = async (email) => {
  if (isMasqueradeEmail(email)) {
    return 'password_verification';
  }

  return 'email_verification';
};

const LOGIN_BACKGROUNDS = [
  {
    publicId: 'France/France-Provence-lavender-field-iStock-1255610084',
    version: 'v1722525004',
  },
  {
    publicId: 'Australia/Australia-QLD-Great-Barrier-Reef-GettyImages-1149874460',
    version: 'v1729894637',
  },
  {
    publicId: 'Africa/Africa-Baobab-tree-sunset-AdobeStock_65542627',
    version: 'v1573142176',
  },
  {
    publicId: 'Scotland/UK-Scotland-Isle-of-Skye-AdobeStock-482137816',
    version: 'v1659554748',
  },
  {
    publicId: 'Chile/Chile-Patagonia-Torres-del-Paine-GettyImages-1151532623',
    version: 'v1739747666',
  },
  {
    publicId: 'Slovenia/Slovenia-Church-Hill-AdobeStock-541069855',
    // Pre-crop to the intended subject before responsive resize.
    crop: 'x_1746,y_341,w_4107,h_2921,c_crop',
  },
] as const;

export type LoginBackgroundSize = 'mobile' | 'desktop';

export type LoginBackgroundUrls = {
  /** Tiny blurred LQIP — paints immediately, no white flash. */
  placeholder: string;
  /** Full hero for soft crossfade once loaded. */
  full: string;
};

/**
 * Luxury hero sweet spot for US clients:
 * - f_auto → AVIF/WebP where supported (big win vs JPEG)
 * - q_auto:best → max Cloudinary quality tier for luxury hero photography
 * - c_limit → never upscales; CSS background-size:cover handles crop
 * - widths cover 3x phones and retina desktops without multi-MB originals
 */
const WIDTH_BY_SIZE: Record<LoginBackgroundSize, number> = {
  mobile: 2000,
  desktop: 2560,
};

const dayOfYear = (date: Date): number => {
  const start = Date.UTC(date.getFullYear(), 0, 0);
  const now = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.floor((now - start) / 86_400_000);
};

const buildCloudinaryUrl = (
  background: (typeof LOGIN_BACKGROUNDS)[number],
  delivery: string,
): string => {
  const transforms = [
    'crop' in background ? background.crop : null,
    delivery,
  ]
    .filter(Boolean)
    .join('/');

  const version = 'version' in background && background.version
    ? `${background.version}/`
    : '';

  return `https://res.cloudinary.com/zicasso/image/upload/${transforms}/${version}${background.publicId}.jpg`;
};

/** Picked once per page load so refreshes rotate in dev without flicker on re-render. */
const DEV_BACKGROUND_INDEX = process.env.NODE_ENV === 'development'
  ? Math.floor(Math.random() * LOGIN_BACKGROUNDS.length)
  : null;

const pickBackground = (date: Date) => {
  const index = DEV_BACKGROUND_INDEX
    ?? dayOfYear(date) % LOGIN_BACKGROUNDS.length;
  return LOGIN_BACKGROUNDS[index];
};

/**
 * Day-stable login hero URLs. Same image all day; rotates through the pool by day-of-year.
 * In local dev, each refresh picks a random background so you can preview the full set.
 * Returns a micro blurred placeholder + full hero for a blur-up crossfade.
 */
export const getLoginBackground = (
  size: LoginBackgroundSize = 'desktop',
  date: Date = new Date(),
): LoginBackgroundUrls => {
  const background = pickBackground(date);
  const width = WIDTH_BY_SIZE[size];

  return {
    placeholder: buildCloudinaryUrl(
      background,
      'f_auto,q_auto:low,e_blur:1000,c_limit,w_40',
    ),
    full: buildCloudinaryUrl(
      background,
      `f_auto,q_auto:best,c_limit,w_${width}`,
    ),
  };
};
